import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { debounceTime, pipe, Subject, Subscription, takeUntil } from 'rxjs';
import { CommonService } from 'src/app/modules/shared/services/common.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DropdownOption } from '../../../../../models/dropdown-option.model';
import { Store } from '@ngrx/store';
import {
  GetAllReportsOptions,
  GetBehaviorType,
  GetClinicsList,
  GetGeneratedReportsList,
  GetGroupsList,
  GetParticipantData,
  GetQuestionnairesData,
  GetPollingReports,
} from '../../core/reports.selectors';
import {
  DownloadReportRequest,
  FetchBehaviorTypeOptionsListRequest,
  FetchClinicsOptionsListRequest,
  FetchGeneratedReportsListRequest,
  FetchGroupsOptionsListRequest,
  FetchParticipantOptionsListRequest,
  FetchQuestionnaireOptionsListRequest,
  FetchReportsOptionsRequest,
  GenerateNewReportsRequest,
  RegenerateReportRequest,
} from '../../core/reports.actions';
import { Sort } from '@angular/material/sort';
import {
  roles,
  sortOrderMap,
} from '../../../../shared/constants/common.constants';
import {
  profile,
  profileData,
} from 'src/app/modules/shared/core/shared.selectors';

@Component({
  selector: 'app-reports-screen',
  templateUrl: './reports-screen.component.html',
  styleUrls: ['./reports-screen.component.css'],
})
export class ReportsScreenComponent implements OnInit, OnDestroy {
  form: FormGroup = new FormGroup({
    reports: new FormControl('', [Validators.required]),
    clientSearch: new FormControl(''),
    groupSearch: new FormControl(''),
    participantSearch: new FormControl(''),
  });

  errorMessageMap: any = {
    reports: {
      required: 'This field is required.',
    },
  };

  errorMessages: any = {};

  reportDropDownOptions: DropdownOption[] = [];
  reportDropDownOptionsLoading: boolean = false;
  clinicsDropDownOptions: DropdownOption[] = [];
  clinicsDropDownOptionsLoading: boolean = false;
  groupsDropDownOptions: DropdownOption[] = [];
  groupsDropDownOptionsLoading: boolean = false;
  behaviorTypeDropDownOptions: DropdownOption[] = [];
  behaviorTypeDropDownOptionsLoading: boolean = false;
  participantsDropDownOptions: DropdownOption[] = [];
  participantsDropDownOptionsLoading: boolean = false;
  questionnaireDropDownOptions: DropdownOption[] = [{ key: 1, value: 'test' }];
  questionnaireDropDownOptionsLoading: boolean = false;
  reportFormMap = {};
  reportsDisplayColumn = ['createdAt', 'type', 'variable', 'status', 'actions'];
  dataSource: any[] = [];
  expandPanel: boolean = true;
  sortColumn: any;
  sortOrder: any;
  sortOrderMap = sortOrderMap;
  loggedInUserRole: any;
  clinicInfo: { clientId?: string; clientName?: string } = null;
  roleMap = roles;
  clientHasNext: boolean = false;
  clientPageNumber: number = 0;
  groupHasNext: boolean = false;
  groupPageNumber: number = 0;
  reportsHasNext: boolean = false;
  reportsPageNumber: number = 0;
  participantHasNext: boolean = false;
  participantPageNumber: number = 0;
  clientOptionsLoading: boolean = false;
  reportLoading: boolean = false;
  isFirstPage: boolean = true;
  pollingReports: { [reportId: number]: boolean } = {};
  reportTypeMap = {
    1: 'participant',
    2: 'participant-measures',
    3: 'participant-e-journal',
    4: 'participant-contract',
    5: 'participant-daily-behaviour-tracker-tool',
    6: 'attendance',
    7: 'group',
    8: 'organization-staff',
    9: 'organization',
    10: 'adult-to-child-relationship',
    11: 'group-schedule',
    12: 'questionnaire',
    13: 'food-items',
  };
  private readonly onDestroy: Subject<any> = new Subject<any>();
  groupSearchSubscriptions: Subscription = new Subscription();

  constructor(
    private commonService: CommonService,
    private route: ActivatedRoute,
    private store: Store,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(FetchReportsOptionsRequest());
    this.store.dispatch(FetchBehaviorTypeOptionsListRequest());
    this.store.dispatch(
      FetchGeneratedReportsListRequest({
        payload: {
          pageNumber: 1,
        },
      }),
    );

    this.store
      .select(profileData)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((profileData: any) => {
        if (profileData.profile) {
          this.loggedInUserRole = profileData.profile.role;
        }
      });

    this.store
      .select(GetGeneratedReportsList)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((reports) => {
        this.reportLoading = reports.loading;
        this.dataSource = reports.list;
        this.reportsHasNext = reports.hasNext;
        this.reportsPageNumber = reports.pageNumber;
        if (this.reportsHasNext && !this.reportLoading) {
          setTimeout(() => {
            // check window height for infinite scroll
            this.commonService.checkInfiniteScrollHeightAndCallNext(
              'table-wrapper',
              () => {
                this.getNext();
              },
            );
          }, 0);
        }
      });
    this.commonService.onNavigationEnd
      .pipe(takeUntil(this.onDestroy))
      .subscribe((d) => {
        const urlParams = {
          params: this.route.snapshot.params,
          queryParams: this.route.snapshot.queryParams,
        };
        const prevUrlParams = this.commonService.urlParams.getValue();

        if (
          d.isRouteChanged ||
          JSON.stringify(prevUrlParams) !== JSON.stringify(urlParams)
        ) {
          this.commonService.urlParams.next(urlParams);
        }
      });

    this.store
      .select(profile)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((profile: any) => {
        this.loggedInUserRole = profile?.role;
        if (this.loggedInUserRole === 2) {
          this.clinicInfo = {
            clientId: profile.clientId,
            clientName: profile.clientName,
          };

          this.clinicsDropDownOptions = [
            {
              key: this.clinicInfo.clientId,
              value: this.clinicInfo.clientName,
            },
          ];
        }

        this.reportFormMap = {
          1: {
            ...(this.loggedInUserRole === this.roleMap.superAdmin
              ? {
                  clientId: {
                    initialValue: '',
                    validators: [],
                    errorMessageMap: {},
                    multiSelect: false,
                    label: 'Organization',
                    placeHolder: 'Organization',
                    enableAllSelection: false,
                  },
                }
              : {}),
            groupId: {
              initialValue: [],
              validators: [],
              errorMessageMap: {},
              multiSelect: true,
              label: 'Groups',
              placeHolder: 'Groups',
            },
          },
          2: {
            ...(this.loggedInUserRole === this.roleMap.superAdmin
              ? {
                  clientId: {
                    initialValue: '',
                    validators: [],
                    errorMessageMap: {
                      required: 'This field is required.',
                    },
                    multiSelect: false,
                    label: 'Organization',
                    placeHolder: 'Organization',
                  },
                }
              : {}),
            groupId: {
              initialValue: '',
              validators: [],
              errorMessageMap: {
                required: 'This field is required.',
              },
              multiSelect: false,
              label: 'Group',
              placeHolder: 'Group',
            },
            participantId: {
              initialValue: [],
              validators: [],
              errorMessageMap: {},
              multiSelect: true,
              label: 'Participants',
              placeHolder: 'Participants',
            },
            fromDate: {
              initialValue: '',
              validators: [Validators.required],
              errorMessageMap: {
                required: 'This field is required.',
              },
            },
            toDate: {
              initialValue: '',
              validators: [Validators.required],
              errorMessageMap: {
                required: 'This field is required.',
              },
            },
          },
          3: {
            ...(this.loggedInUserRole === this.roleMap.superAdmin
              ? {
                  clientId: {
                    initialValue: null,
                    validators: [],
                    errorMessageMap: {
                      required: 'This field is required.',
                    },
                    multiSelect: false,
                    label: 'Organization',
                    placeHolder: 'Organization',
                  },
                }
              : {}),
            groupId: {
              initialValue: [],
              validators: [],
              errorMessageMap: {
                required: 'This field is required.',
              },
              multiSelect: true,
              label: 'Group',
              placeHolder: 'Group',
            },
            participantId: {
              initialValue: [],
              validators: [],
              errorMessageMap: {},
              multiSelect: true,
              label: 'Participant',
              placeHolder: 'Participant',
            },
            fromDate: {
              initialValue: '',
              validators: [Validators.required],
              errorMessageMap: {
                required: 'This field is required.',
              },
            },
            toDate: {
              initialValue: '',
              validators: [Validators.required],
              errorMessageMap: {
                required: 'This field is required.',
              },
            },
            behaviorType: {
              initialValue: [],
              validators: [],
              errorMessageMap: {},
            },
          },
          4: {
            ...(this.loggedInUserRole === this.roleMap.superAdmin
              ? {
                  clientId: {
                    initialValue: null,
                    validators: [Validators.required],
                    errorMessageMap: {
                      required: 'This field is required.',
                    },
                    multiSelect: false,
                    label: 'Organization',
                    placeHolder: 'Organization',
                  },
                }
              : {}),
            groupId: {
              initialValue: [],
              validators: [Validators.required],
              errorMessageMap: {
                required: 'This field is required.',
              },
              multiSelect: true,
              label: 'Groups',
              placeHolder: 'Groups',
            },
            participantId: {
              initialValue: [],
              validators: [],
              errorMessageMap: {},
              multiSelect: true,
              label: 'Participants',
              placeHolder: 'Participants',
            },
          },
          5: {
            ...(this.loggedInUserRole === this.roleMap.superAdmin
              ? {
                  clientId: {
                    initialValue: null,
                    validators: [Validators.required],
                    errorMessageMap: {
                      required: 'This field is required.',
                    },
                    multiSelect: false,
                    label: 'Organization',
                    placeHolder: 'Organization',
                  },
                }
              : {}),
            groupId: {
              initialValue: [],
              validators: [Validators.required],
              errorMessageMap: {
                required: 'This field is required.',
              },
              multiSelect: true,
              label: 'Group',
              placeHolder: 'Group',
            },
            participantId: {
              initialValue: [],
              validators: [],
              errorMessageMap: {},
              multiSelect: true,
              label: 'Participant',
              placeHolder: 'Participant',
            },
            fromDate: {
              initialValue: '',
              validators: [Validators.required],
              errorMessageMap: {
                required: 'This field is required.',
              },
            },
            toDate: {
              initialValue: '',
              validators: [Validators.required],
              errorMessageMap: {
                required: 'This field is required.',
              },
            },
          },
          6: {
            ...(this.loggedInUserRole === this.roleMap.superAdmin
              ? {
                  clientId: {
                    initialValue: [],
                    validators: [],
                    errorMessageMap: {},
                    multiSelect: true,
                    label: 'Organizations',
                    placeHolder: 'Organizations',
                  },
                }
              : {}),
            groupId: {
              initialValue: [],
              validators: [],
              errorMessageMap: {},
              multiSelect: true,
              label: 'Groups',
              placeHolder: 'Groups',
            },
            participantId: {
              initialValue: [],
              validators: [],
              errorMessageMap: {},
              multiSelect: true,
              label: 'Participants',
              placeHolder: 'Participants',
            },
            fromDate: {
              initialValue: '',
              validators: [Validators.required],
              errorMessageMap: {
                required: 'This field is required.',
              },
            },
            toDate: {
              initialValue: '',
              validators: [Validators.required],
              errorMessageMap: {
                required: 'This field is required.',
              },
            },
          },
          7: {
            ...(this.loggedInUserRole === this.roleMap.superAdmin
              ? {
                  clientId: {
                    initialValue: '',
                    validators: [],
                    errorMessageMap: {},
                    multiSelect: false,
                    label: 'Organization',
                    placeHolder: 'Organization',
                  },
                }
              : {}),
            groupId: {
              initialValue: [],
              validators: [],
              errorMessageMap: {},
              multiSelect: true,
              label: 'Groups',
              placeHolder: 'Groups',
            },
          },
          8: {
            ...(this.loggedInUserRole === this.roleMap.superAdmin
              ? {
                  clientId: {
                    initialValue: '',
                    validators: [],
                    errorMessageMap: {},
                    multiSelect: true,
                    label: 'Organizations',
                    placeHolder: 'Organizations',
                  },
                }
              : {}),
            groupId: {
              initialValue: [],
              validators: [],
              errorMessageMap: {},
              multiSelect: true,
              label: 'Groups',
              placeHolder: 'Groups',
            },
          },
          9: {
            ...(this.loggedInUserRole === this.roleMap.superAdmin
              ? {
                  clientId: {
                    initialValue: [],
                    validators: [],
                    errorMessageMap: {
                      required: 'This field is required.',
                    },
                    multiSelect: true,
                    label: 'Organizations',
                    placeHolder: 'Organizations',
                    enableAllSelection: false,
                  },
                }
              : {}),
          },
          10: {
            ...(this.loggedInUserRole === this.roleMap.superAdmin
              ? {
                  clientId: {
                    initialValue: [],
                    validators: [],
                    errorMessageMap: {},
                    multiSelect: true,
                    label: 'Organizations',
                    placeHolder: 'Organizations',
                  },
                }
              : {}),
            groupId: {
              initialValue: [],
              validators: [],
              errorMessageMap: {},
              multiSelect: true,
              label: 'Groups',
              placeHolder: 'Groups',
            },
            participantId: {
              initialValue: [],
              validators: [],
              errorMessageMap: {},
              multiSelect: true,
              label: 'Participants',
              placeHolder: 'Participants',
            },
          },
          11: {
            ...(this.loggedInUserRole === this.roleMap.superAdmin
              ? {
                  clientId: {
                    initialValue: '',
                    validators: [],
                    errorMessageMap: {},
                    multiSelect: true,
                    label: 'Organizations',
                    placeHolder: 'Organizations',
                  },
                }
              : {}),
            groupId: {
              initialValue: [],
              validators: [],
              errorMessageMap: {},
              multiSelect: true,
              label: 'Groups',
              placeHolder: 'Groups',
            },
          },
          12: {
            surveyId: {
              initialValue: null,
              validators: [],
              errorMessageMap: {},
              multiSelect: false,
              label: 'Questionnaire',
              placeHolder: 'Questionnaire',
            },
          },
          13: {},
        };
      });

    this.store
      .select(GetAllReportsOptions)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((options) => {
        this.reportDropDownOptionsLoading = options.loading;
        this.reportDropDownOptions = options.list;
      });

    this.store
      .select(GetClinicsList)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((options) => {
        if (this.loggedInUserRole === this.roleMap.superAdmin) {
          this.clinicsDropDownOptions = options.list;
          this.clinicsDropDownOptionsLoading = options.loading;
          this.clientHasNext = options.hasNext;
          this.clientPageNumber = options.pageNumber;
        }
      });

    this.store
      .select(GetGroupsList)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((options) => {
        this.groupsDropDownOptionsLoading = options.loading;
        this.groupsDropDownOptions = options.list;
        this.groupHasNext = options.hasNext;
        this.groupPageNumber = options.pageNumber;
      });

    this.store
      .select(GetBehaviorType)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((options) => {
        this.behaviorTypeDropDownOptionsLoading = options.loading;
        this.behaviorTypeDropDownOptions = options.list;
      });

    this.store
      .select(GetQuestionnairesData)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((options) => {
        this.questionnaireDropDownOptionsLoading = options.loading;
        this.questionnaireDropDownOptions = options.list;
      });

    this.store
      .select(GetParticipantData)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((options) => {
        this.participantsDropDownOptionsLoading = options.loading;
        this.participantsDropDownOptions = options.list;
        this.participantHasNext = options.hasNext;
        this.participantPageNumber = options.pageNumber;
      });

    this.store
      .select(GetPollingReports)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((pollingReports) => {
        this.pollingReports = pollingReports;
      });

    this.addReportsSubscriptions();
  }

  clientSearchEventSubscriptions() {
    this.form.controls['clientSearch'].valueChanges
      .pipe(takeUntil(this.onDestroy), debounceTime(1000))
      .subscribe((searchedValue) => {
        if (!this.clientOptionsLoading) {
          this.fetchClientsList(1, searchedValue);
        }
      });
  }

  groupSearchEventSubscriptions() {
    this.groupSearchSubscriptions = this.form.controls[
      'groupSearch'
    ]?.valueChanges
      .pipe(debounceTime(1000))
      .subscribe((searchedValue) => {
        if (
          this.loggedInUserRole === this.roleMap.superAdmin &&
          this.form.value.clientId &&
          !this.groupsDropDownOptionsLoading
        ) {
          this.fetchGroupList(1, this.form.value.clientId, searchedValue);
        }
        if (this.loggedInUserRole === this.roleMap.clientAdmin) {
          this.fetchGroupList(1, this.form.value.clientId, searchedValue);
        }
      });
  }

  participantSearchEventSubscriptions() {
    this.groupSearchSubscriptions = this.form.controls[
      'participantSearch'
    ]?.valueChanges
      .pipe(debounceTime(1000))
      .subscribe((searchedValue) => {
        if (
          this.form.value.groupId &&
          !this.participantsDropDownOptionsLoading
        ) {
          this.fetchParticipantList(1, this.form.value.groupId, searchedValue);
        }
      });
  }

  groupSelectedEventSubscriptions() {
    this.form
      .get('groupId')
      .valueChanges.pipe(takeUntil(this.onDestroy), debounceTime(1000))
      .subscribe((groupIds) => {
        this.participantsDropDownOptions = [];
        if (groupIds) {
          this.form.get('participantId').setValue([], { eventEmit: false });
          this.form.get('participantSearch').setValue('', { emitEvent: false });
          this.participantsDropDownOptions = [];
          this.fetchParticipantList(1, groupIds);
        }
      });
  }

  clientSelectEventSubscriptions() {
    this.form.controls['clientId']?.valueChanges
      .pipe(takeUntil(this.onDestroy), debounceTime(1000))
      .subscribe((selectedClientId) => {
        this.groupsDropDownOptions = [];
        this.participantsDropDownOptions = [];
        if (this.form.get('groupId')) {
          this.form
            .get('groupId')
            .setValue(
              this.reportFormMap[this.form.get('reports').value].initialValue,
            );
          this.fetchGroupList(1, selectedClientId);
        }
      });
  }

  fromDateSelectEventSubscription() {
    this.form.controls['fromDate']?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((selectedClientId) => {
        this.form.get('toDate').setValue('', { emitEvent: false });
      });
  }

  addReportsSubscriptions() {
    this.form
      .get('reports')
      .valueChanges.pipe(takeUntil(this.onDestroy))
      .subscribe((values) => {
        this.clinicsDropDownOptions = [];
        this.groupsDropDownOptions = [];
        this.participantsDropDownOptions = [];
        this.form.get('clientSearch').patchValue('', { emitEvent: false });
        this.form.get('groupSearch').patchValue('', { emitEvent: false });
        if (this.loggedInUserRole === this.roleMap.superAdmin) {
          this.fetchClientsList(1);
        }
        if (this.loggedInUserRole === this.roleMap.clientAdmin) {
          this.fetchGroupList(1);
        }
        this.addDynamicFormControlsAccordingToReportSelection(values);
      });
  }

  fetchClientsList(pageNumber: number, searchValue?: string) {
    this.store.dispatch(
      FetchClinicsOptionsListRequest({
        payload: {
          pageNumber: pageNumber,
          search: searchValue ? searchValue : this.form.value?.clientSearch,
          shouldInclude: this.form.get('clientId')
            ? this.form.get('clientId').value
            : [],
        },
      }),
    );
  }

  fetchSurveysList() {
    this.store.dispatch(FetchQuestionnaireOptionsListRequest());
  }

  fetchGroupList(
    pageNumber: number,
    selectedClientId?: string,
    searchedValue?: string,
  ) {
    this.store.dispatch(
      FetchGroupsOptionsListRequest({
        payload: {
          clientId: selectedClientId ? selectedClientId : null,
          pageNumber: pageNumber,
          search: searchedValue ? searchedValue : this.form.value?.groupSearch,
          shouldInclude: this.form.value.groupId,
        },
      }),
    );
  }

  fetchClinicsNextPageOptions() {
    this.fetchClientsList(
      this.clientPageNumber + 1,
      this.form.value.clientSearch,
    );
  }

  fetchGroupsNextPageOptions() {
    this.fetchGroupList(
      this.groupPageNumber + 1,
      this.form.get('clientId') ? this.form.get('clientId').value : null,
    );
  }

  fetchParticipantNextPageOptions() {
    if (this.form.get('groupId').value) {
      this.fetchParticipantList(
        this.participantPageNumber + 1,
        this.form.get('groupId') ? this.form.get('groupId').value : null,
      );
    }
  }

  addDynamicFormControlsAccordingToReportSelection(values) {

    this.form = new FormGroup({
      reports: new FormControl(values, [Validators.required]),
      clientSearch: new FormControl(''),
      groupSearch: new FormControl(''),
      participantSearch: new FormControl(''),
    });

    this.errorMessageMap = {
      reports: {
        required: 'This Field is required',
      },
    };
    this.errorMessages = {};
    this.addReportsSubscriptions();
    if (values != '') {
      setTimeout(() => {
        Object.keys(this.reportFormMap[values]).forEach((keys) => {
          const formControl = new FormControl(
            this.reportFormMap[values][keys].initialValue,
            this.reportFormMap[values][keys].validators,
          );
          this.form.addControl(keys, formControl);
          this.errorMessageMap[keys] =
            this.reportFormMap[values][keys].errorMessageMap;
          this.changeDetectorRef?.detectChanges();
        });

        this.clientSearchEventSubscriptions();
        this.clientSelectEventSubscriptions();
        this.groupSearchEventSubscriptions();
        if (
          values === 2 ||
          values === 6 ||
          values === 4 ||
          values === 3 ||
          values === 5 ||
          values === 10
        ) {
          this.groupSelectedEventSubscriptions();
          this.participantSearchEventSubscriptions();
        }
        if (values === 2 || values === 3 || values === 5 || values === 6) {
          this.fromDateSelectEventSubscription();
        }
        if (values === 12) {
          this.fetchSurveysList();
        }
      }, 0);
    }
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...this.commonService.checkFormValidation(
        this.form,
        this.errorMessageMap,
        currentField,
      ),
    };
  }

  fetchParticipantList(pageNumber: number, groupId: number, search?: string) {
    this.store.dispatch(
      FetchParticipantOptionsListRequest({
        payload: {
          groupId: groupId,
          pageNumber: pageNumber,
          username: search,
          shouldInclude: this.form.value.participantId,
        },
      }),
    );
  }

  downloadButtonClicked() {
    const {
      reports,
      clientSearch,
      groupSearch,
      participantSearch,
      ...payload
    } = this.form.getRawValue();

    Object.keys(payload).map((key) => {
      if (!payload[key]) {
        delete payload[key];
      }
    });
    this.store.dispatch(
      GenerateNewReportsRequest({
        payload: {
          reportType: this.reportTypeMap[this.form.value.reports],
          reportRequestPayload: payload,
        },
      }),
    );
  }

  //this will be changed once we have api
  getToolTipList(formControlName: string, selectedValues: any[]) {
    if (selectedValues) {
      const selectedOptions: any[] = this[
        formControlName + 'DropDownOptions'
      ].filter((element) => selectedValues?.includes(element.key));
      return selectedOptions.map((selected) => selected.value).join(', ');
    } else {
      return '';
    }
  }

  sortData(sort: Sort) {
    this.isFirstPage = true;
    this.sortColumn = sort.active;
    this.sortOrder = sort.direction;
    this.store.dispatch(
      FetchGeneratedReportsListRequest({
        payload: {
          pageNumber: 1,
          ...(this.sortColumn && this.sortOrder
            ? { sortColumn: this.sortColumn }
            : {}),
          ...(this.sortColumn && this.sortOrder
            ? { sortOrder: this.getSortOrder() }
            : {}),
        },
      }),
    );
  }

  getSortOrder(): number {
    return this.sortOrderMap[this.sortOrder];
  }

  getNext() {
    this.isFirstPage = false;
    if (this.reportsHasNext && !this.reportLoading) {
      this.store.dispatch(
        FetchGeneratedReportsListRequest({
          payload: {
            pageNumber: this.reportsPageNumber + 1,
            ...(this.sortColumn && this.sortOrder
              ? { sortColumn: this.sortColumn }
              : {}),
            ...(this.sortColumn && this.sortOrder
              ? { sortOrder: this.getSortOrder() }
              : {}),
          },
        }),
      );
    }
  }

  downloadReports(rowData: any) {
    this.store.dispatch(
      DownloadReportRequest({
        payload: {
          reportId: rowData.id,
        },
      }),
    );
  }

  reGeneratedReports(rowData: any) {
    this.store.dispatch(
      RegenerateReportRequest({
        payload: {
          reportId: rowData.id,
        },
      }),
    );
  }

  getToolTipsDetails(selectedIds) {
    return selectedIds?.map((item) => item.name).join(', ');
  }

  /**
   * Check if a report is currently being polled
   */
  isReportPolling(reportId: number): boolean {
    return !!this.pollingReports[reportId];
  }

  /**
   * Check if report is ready for download
   * Status key: -1=Queued, 1=Pending, 2=AvailableForDownload, 3=Failed, 4=Downloaded
   */
  isReportReadyForDownload(statusKey: number): boolean {
    return statusKey === 2; // Available for download
  }

  /**
   * Check if report generation is in progress
   */
  isReportGenerating(statusKey: number): boolean {
    return statusKey === -1 || statusKey === 1; // Queued or Pending
  }

  /**
   * Check if report generation failed
   */
  isReportFailed(statusKey: number): boolean {
    return statusKey === 3; // Failed
  }

  /**
   * Get status display text with better messaging
   */
  getStatusDisplayText(statusKey: number, statusValue: string): string {
    if (this.isReportGenerating(statusKey)) {
      return 'Generating...';
    }
    return statusValue;
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
